import type {EstablishedAhpSession} from './contracts'
import {readSessionApprovalState, type SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'

/**
 * Brings an established session to `mode` with a `session/configChanged` only when
 * the host does not already report it in `SessionState.config.values.mode`. A
 * session created with `config.mode` is already in that mode, so this sends
 * nothing; an attached session or a host that does not publish `mode` gets a
 * single write.
 */
export async function ensureSessionMode(
  session: Pick<EstablishedAhpSession, 'view' | 'commands'>,
  mode: string | undefined,
): Promise<void> {
  if (!mode || session.view.getSnapshot().sessionState?.config?.values?.mode === mode) return
  await session.commands.setMode(mode)
}

/**
 * Brings an established session to `approvalMode` with `session.commands.setApprovalMode`
 * only when the host does not already report it selected. A session created with
 * `config.approvalMode` is already in that mode, so this sends nothing; an attached
 * session or a host that does not echo the selection gets a single write.
 */
export async function ensureApprovalMode(
  session: Pick<EstablishedAhpSession, 'view' | 'commands'>,
  approvalMode: SessionApprovalMode | undefined,
): Promise<void> {
  if (!approvalMode) return
  const state = session.view.getSnapshot().sessionState
  const approval = readSessionApprovalState(state)
  if (!approval.selectedMutable && approval.selected === approvalMode) return
  // A launch record can predate a policy change. On writable hosts, narrow its
  // prohibited preference to the current constraints even if it is still selected.
  const available = approval.available
  const permittedMode =
    approvalMode === 'manual' || available === undefined || available.includes(approvalMode)
      ? approvalMode
      : available.includes('assisted')
        ? 'assisted'
        : 'manual'
  if (approval.selected === permittedMode) return
  await session.commands.setApprovalMode(permittedMode)
}
